import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { 
  Book, Feather, Quotes, ArrowRight, EnvelopeSimple,
  Users, BookOpen, Sparkle, TrendUp
} from '@phosphor-icons/react';
import GlobalHeader from '../components/GlobalHeader';
import Footer from '../components/Footer';
import { proxyImage } from '../lib/imageProxy';
import { createSafeRichHTML } from '../lib/sanitize';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to strip HTML tags
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Design Guidelines Image URLs
const IMAGES = {
  heroTexture: "https://images.pexels.com/photos/19582307/pexels-photo-19582307.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  generalTexture: "https://images.pexels.com/photos/20010380/pexels-photo-20010380.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  poemOfDay: "https://images.unsplash.com/photo-1628962691167-27b7db9997e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBjYWxsaWdyYXBoeSUyMGluayUyMHBhcGVyfGVufDB8fHx8MTc4MzI2ODU0NHww&ixlib=rb-4.1.0&q=85",
  authorPortrait1: "https://images.pexels.com/photos/6054690/pexels-photo-6054690.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  authorPortrait2: "https://images.pexels.com/photos/5402000/pexels-photo-5402000.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

export const HomeEnhanced = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroIdx, setHeroIdx] = useState(0);
  const [stats, setStats] = useState({ poems: 0, authors: 0, collections: 0, readers: 0 });
  const [params] = useSearchParams();
  const preview = params.get('preview') === '1';

  useEffect(() => {
    const url = preview
      ? `${API}/api/admin/cms/homepage/preview`
      : `${API}/api/homepage`;
    axios.get(url, { withCredentials: preview })
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null))
      .finally(() => setLoading(false));
  }, [preview]);

  // Fetch platform stats
  useEffect(() => {
    axios.get(`${API}/api/stats`)
      .then(r => setStats(r.data))
      .catch(() => {});
  }, []);

  // Apply SEO meta tags
  useEffect(() => {
    if (!config?.seo) return;
    const s = config.seo;
    if (s.meta_title) document.title = s.meta_title;
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', s.meta_description);
    setMeta('keywords', s.meta_keywords);
  }, [config]);

  // Hero autoplay
  useEffect(() => {
    const slides = config?.hero?.slides || [];
    if (slides.length <= 1) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [config]);

  if (loading) return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      <div className="flex justify-center py-32">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full" />
      </div>
    </div>
  );

  if (!config) return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      <p className="text-center py-32 text-muted-foreground">Homepage not configured.</p>
    </div>
  );

  const sections = (config.sections || []).filter((s) => s.enabled).sort((a,b)=>a.order-b.order);
  const slides = config.hero?.slides || [];

  return (
    <div className="min-h-screen bg-background" data-testid="home-enhanced">
      <GlobalHeader />
      {preview && (
        <div className="bg-amber-100 dark:bg-amber-950/40 border-b border-amber-300 text-amber-900 dark:text-amber-200 text-center text-xs py-2" data-testid="preview-banner">
          Preview mode — showing draft (not yet published).
        </div>
      )}

      {/* HERO SECTION - Enhanced with Traditional Design */}
      {slides.length > 0 && (
        <HeroSection slide={slides[heroIdx]} slides={slides} heroIdx={heroIdx} setHeroIdx={setHeroIdx} />
      )}

      {/* PLATFORM STATS SECTION */}
      <PlatformStatsSection stats={stats} />

      {/* FEATURED QUOTE SECTION */}
      <FeaturedQuoteSection />

      {/* LEGENDARY VOICES - Enhanced Author Cards */}
      {sections.find(s => s.type.includes('author')) && (
        <LegendaryVoicesSection section={sections.find(s => s.type.includes('author'))} />
      )}

      {/* TRENDING NOW - Bento Grid */}
      {sections.find(s => s.type.includes('recent') || s.type.includes('trending')) && (
        <TrendingNowSection section={sections.find(s => s.type.includes('recent') || s.type.includes('trending'))} />
      )}

      {/* POEM OF THE DAY - Split Layout */}
      {sections.find(s => s.type === 'poem_of_day') && (
        <PoemOfDaySection section={sections.find(s => s.type === 'poem_of_day')} />
      )}

      {/* SOCIAL PROOF - Marquee */}
      <SocialProofSection />

      {/* LATEST ADDITIONS */}
      <LatestAdditionsSection />

      {/* Newsletter */}
      {config.newsletter?.enabled && (
        <NewsletterSection newsletter={config.newsletter} />
      )}

      {/* MASSIVE DARK FOOTER */}
      <MassiveFooter />
    </div>
  );
};

/* ========== HERO SECTION ========== */
function HeroSection({ slide, slides, heroIdx, setHeroIdx }) {
  return (
    <motion.section 
      initial="hidden"
      animate="visible"
      variants={fadeUpVariants}
      className="relative min-h-[85vh] flex items-center justify-start overflow-hidden border-b border-border"
      data-testid="hero-section"
    >
      {/* Background Texture */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url(${IMAGES.heroTexture})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mixBlendMode: 'multiply'
        }}
      />
      
      {/* Massive Devanagari Watermark */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 font-serif text-[20rem] opacity-5 pointer-events-none select-none text-primary">
        काव्य
      </div>

      {/* Content - Left Aligned */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="max-w-3xl">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary mb-4 block" data-testid="hero-subtitle">
            {slide?.subtitle || "A HOME FOR HINDI LITERATURE"}
          </span>
          
          <h1 
            className="font-serif tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-foreground font-semibold mb-6 leading-[1.1]"
            data-testid="hero-title"
          >
            {slide?.title || "The Soul of Hindi Literature"}
          </h1>
          
          <p className="font-sans leading-relaxed text-foreground text-base md:text-lg mb-8 max-w-xl" data-testid="hero-description">
            {slide?.description || "Discover the legendary voices of Hindi literature — from Kabir to Premchand, Mahadevi Verma to Agyeya."}
          </p>

          {slide?.cta_link && slide?.cta_text && (
            <Link 
              to={slide.cta_link} 
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-8 py-4 text-sm font-semibold hover:bg-primary/90 transition-colors"
              data-testid="hero-cta-button"
            >
              {slide.cta_text} <ArrowRight size={16} weight="bold" />
            </Link>
          )}

          {/* Slide Indicators */}
          {slides.length > 1 && (
            <div className="flex gap-2 mt-12">
              {slides.map((_, i) => (
                <button 
                  key={i} 
                  onClick={() => setHeroIdx(i)} 
                  className={`h-1 rounded-full transition-all ${i === heroIdx ? 'bg-primary w-12' : 'bg-border w-6'}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}

/* ========== PLATFORM STATS SECTION ========== */
function PlatformStatsSection({ stats }) {
  const statItems = [
    { label: "Poems & Stories", value: stats.poems || "10,000+", icon: BookOpen },
    { label: "Legendary Authors", value: stats.authors || "500+", icon: Users },
    { label: "Curated Collections", value: stats.collections || "50+", icon: Book },
    { label: "Active Readers", value: stats.readers || "25,000+", icon: Sparkle },
  ];

  return (
    <motion.section 
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpVariants}
      className="py-0 border-b border-border"
      data-testid="platform-stats-section"
    >
      <div className="grid grid-cols-2 md:grid-cols-4">
        {statItems.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div 
              key={idx}
              className="text-center py-16 px-6 border-r border-b border-border last:border-r-0 md:border-b-0 hover:bg-muted/30 transition-colors"
            >
              <Icon size={32} className="mx-auto mb-4 text-primary" weight="duotone" />
              <div className="font-serif text-5xl font-semibold text-foreground mb-2">
                {stat.value}
              </div>
              <div className="uppercase tracking-[0.2em] text-xs font-semibold text-muted-foreground">
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}

/* ========== FEATURED QUOTE SECTION ========== */
function FeaturedQuoteSection() {
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpVariants}
      className="relative py-32 px-6 overflow-hidden border-b border-border"
      data-testid="featured-quote-section"
    >
      {/* Background Texture */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url(${IMAGES.generalTexture})`,
          backgroundSize: 'cover',
          mixBlendMode: 'multiply'
        }}
      />
      
      <div className="relative max-w-4xl mx-auto text-center">
        <Quotes size={48} weight="fill" className="mx-auto mb-8 text-primary/30" />
        
        <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl leading-relaxed text-foreground mb-8 tracking-tight">
          "जो बीत गई सो बात गई"
        </blockquote>
        
        <p className="font-sans text-lg text-muted-foreground italic mb-4">
          "What is past is past"
        </p>
        
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-primary">
          — Harivansh Rai Bachchan
        </p>
      </div>
    </motion.section>
  );
}

/* ========== LEGENDARY VOICES SECTION ========== */
function LegendaryVoicesSection({ section }) {
  const items = section?.resolved_items || [];
  
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpVariants}
      className="py-24 px-6 bg-muted/20"
      data-testid="legendary-voices-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h2 className="font-serif tracking-tighter text-4xl sm:text-5xl text-foreground font-semibold mb-3">
            {section?.title || "Legendary Voices"}
          </h2>
          <p className="text-muted-foreground text-lg">
            {section?.description || "Immortal poets and storytellers"}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {items.slice(0, 5).map((author, idx) => (
            <Link
              key={author.author_id || idx}
              to={`/author/${author.slug || author.author_id}`}
              className="group"
            >
              <div className="relative overflow-hidden rounded-full aspect-square mb-4 border-4 border-border bg-muted hover:-translate-y-1 transition-transform">
                {author.photograph ? (
                  <img 
                    src={proxyImage(author.photograph)} 
                    alt={author.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                    <Feather size={48} className="text-primary/40" weight="duotone" />
                  </div>
                )}
              </div>
              <div className="text-center">
                <p className="font-serif text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                  {author.name}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {author.literary_movement || author.literary_period || ""}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {section?.view_all_link && (
          <div className="text-center mt-12">
            <Link 
              to={section.view_all_link}
              className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
            >
              View all authors <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </motion.section>
  );
}

/* ========== TRENDING NOW SECTION ========== */
function TrendingNowSection({ section }) {
  const items = section?.resolved_items || [];
  
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpVariants}
      className="py-24 px-6 border-b border-border"
      data-testid="trending-now-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-12">
          <div>
            <h2 className="font-serif tracking-tighter text-4xl sm:text-5xl text-foreground font-semibold mb-3">
              {section?.title || "Trending Now"}
            </h2>
            <p className="text-muted-foreground text-lg">
              {section?.description || "Most-read works this week"}
            </p>
          </div>
          {section?.view_all_link && (
            <Link 
              to={section.view_all_link}
              className="inline-flex items-center gap-1 text-primary font-semibold hover:underline whitespace-nowrap"
            >
              View all <ArrowRight size={14} />
            </Link>
          )}
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.slice(0, 6).map((work, idx) => (
            <Link
              key={work.work_id || idx}
              to={`/work/${work.slug || work.work_id}`}
              className={`group border border-border rounded-lg p-6 hover:border-primary/50 transition-colors bg-background ${
                idx === 0 ? 'md:col-span-2 md:row-span-2 p-12' : ''
              }`}
            >
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary block mb-3">
                {work.work_type || "POEM"}
              </span>
              <h3 className={`font-serif font-semibold text-foreground group-hover:text-primary transition-colors mb-3 ${
                idx === 0 ? 'text-3xl' : 'text-xl'
              }`}>
                {work.title}
              </h3>
              {work.description && (
                <p className={`text-muted-foreground ${idx === 0 ? 'text-base line-clamp-3' : 'text-sm line-clamp-2'}`}>
                  {stripHtml(work.description)}
                </p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

/* ========== POEM OF THE DAY SECTION ========== */
function PoemOfDaySection({ section }) {
  const poem = section?.resolved_items?.[0];
  
  if (!poem) return null;
  
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpVariants}
      className="py-0 border-b border-border"
      data-testid="poem-of-day-section"
    >
      <div className="grid md:grid-cols-2">
        {/* Image Half */}
        <div 
          className="h-[400px] md:h-auto bg-muted"
          style={{
            backgroundImage: `url(${IMAGES.poemOfDay})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Content Half */}
        <div className="p-12 md:p-16 flex flex-col justify-center">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary mb-4 block">
            POEM OF THE DAY
          </span>
          
          <h3 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-6">
            {poem.title}
          </h3>
          
          <div className="editorial-drop-cap font-serif text-lg leading-relaxed text-foreground mb-6 line-clamp-6">
            {stripHtml(poem.content)?.slice(0, 300) || stripHtml(poem.description) || "A beautiful verse awaits..."}
          </div>
          
          {poem.author_name && (
            <p className="text-sm text-muted-foreground mb-6">
              — {poem.author_name}
            </p>
          )}
          
          <Link
            to={`/work/${poem.slug || poem.work_id}`}
            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
          >
            Read full poem <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </motion.section>
  );
}

/* ========== SOCIAL PROOF SECTION ========== */
function SocialProofSection() {
  const testimonials = [
    "A treasure trove of Hindi poetry",
    "Beautifully curated collection",
    "My daily dose of inspiration",
    "Connecting me to my roots",
    "Pure literary excellence",
  ];
  
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpVariants}
      className="py-16 border-b border-border overflow-hidden"
      data-testid="social-proof-section"
    >
      <Marquee speed={30} gradient={false}>
        {testimonials.map((text, idx) => (
          <span key={idx} className="mx-8 font-serif text-2xl md:text-3xl text-muted-foreground">
            {text} <span className="text-primary">❖</span>
          </span>
        ))}
      </Marquee>
    </motion.section>
  );
}

/* ========== LATEST ADDITIONS SECTION ========== */
function LatestAdditionsSection() {
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpVariants}
      className="py-24 px-6 bg-muted/10"
      data-testid="latest-additions-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-12">
          <div>
            <h2 className="font-serif tracking-tighter text-4xl sm:text-5xl text-foreground font-semibold mb-3">
              Latest Additions
            </h2>
            <p className="text-muted-foreground text-lg">
              Newly added to our literary collection
            </p>
          </div>
          <Link 
            to="/library"
            className="inline-flex items-center gap-1 text-primary font-semibold hover:underline whitespace-nowrap"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((idx) => (
            <div key={idx} className="border border-border rounded-lg p-6 hover:border-primary/50 transition-colors bg-background">
              <TrendUp size={24} className="text-primary mb-4" weight="duotone" />
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary block mb-3">
                NEW
              </span>
              <h3 className="font-serif text-xl font-semibold text-foreground mb-3">
                Sample Work {idx}
              </h3>
              <p className="text-sm text-muted-foreground">
                Recently added literary work awaiting discovery...
              </p>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

/* ========== NEWSLETTER SECTION ========== */
function NewsletterSection({ newsletter }) {
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpVariants}
      className="relative py-24 px-6 overflow-hidden border-b border-border"
      data-testid="newsletter-section"
    >
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url(${IMAGES.generalTexture})`,
          backgroundSize: 'cover',
          mixBlendMode: 'multiply'
        }}
      />
      
      <div className="relative max-w-3xl mx-auto text-center">
        <EnvelopeSimple size={48} className="mx-auto mb-6 text-primary" weight="duotone" />
        
        <h3 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-4">
          {newsletter.heading || "Join our literary journey"}
        </h3>
        
        <p className="text-lg text-muted-foreground mb-8">
          {newsletter.description || "Weekly Hindi poems and stories delivered to your inbox."}
        </p>
        
        {newsletter.cta_link && (
          <Link
            to={newsletter.cta_link}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-8 py-4 text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            {newsletter.cta_text || "Subscribe"} <ArrowRight size={16} weight="bold" />
          </Link>
        )}
      </div>
    </motion.section>
  );
}

/* ========== MASSIVE DARK FOOTER ========== */
function MassiveFooter() {
  return (
    <footer className="relative py-24 px-6 bg-foreground text-background overflow-hidden">
      {/* Huge Kavyalekh Text */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 font-serif text-[15vw] font-bold opacity-10 pointer-events-none select-none whitespace-nowrap">
        Kavyalekh
      </div>
      
      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-12 mb-16">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="inline-block mb-6">
              <h3 className="font-serif text-3xl font-bold">Kavyalekh</h3>
            </Link>
            <p className="text-background/70 leading-relaxed max-w-md">
              A premier platform celebrating Hindi literature — from legendary classics to contemporary voices.
            </p>
            <p className="text-background/50 text-sm mt-4">
              support@kavyalekh.com
            </p>
          </div>

          {/* Explore */}
          <div>
            <h4 className="uppercase tracking-[0.2em] text-xs font-semibold mb-6 text-background/70">Explore</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/authors" className="hover:text-primary transition-colors">Authors</Link></li>
              <li><Link to="/browse?category=Poem" className="hover:text-primary transition-colors">Poems</Link></li>
              <li><Link to="/browse?category=Story" className="hover:text-primary transition-colors">Stories</Link></li>
              <li><Link to="/collections" className="hover:text-primary transition-colors">Collections</Link></li>
              <li><Link to="/competitions" className="hover:text-primary transition-colors">Competitions</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="uppercase tracking-[0.2em] text-xs font-semibold mb-6 text-background/70">Company</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/page/about" className="hover:text-primary transition-colors">About</Link></li>
              <li><Link to="/page/mission" className="hover:text-primary transition-colors">Mission</Link></li>
              <li><Link to="/page/team" className="hover:text-primary transition-colors">Our Team</Link></li>
              <li><Link to="/page/partnerships" className="hover:text-primary transition-colors">Partnerships</Link></li>
              <li><Link to="/page/advertise" className="hover:text-primary transition-colors">Advertise</Link></li>
            </ul>
          </div>

          {/* Writers */}
          <div>
            <h4 className="uppercase tracking-[0.2em] text-xs font-semibold mb-6 text-background/70">Writers</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/page/become-a-writer" className="hover:text-primary transition-colors">Become a Writer</Link></li>
              <li><Link to="/page/writer-guidelines" className="hover:text-primary transition-colors">Writer Guidelines</Link></li>
              <li><Link to="/page/reader-guidelines" className="hover:text-primary transition-colors">Reader Guidelines</Link></li>
              <li><Link to="/page/editorial-policy" className="hover:text-primary transition-colors">Editorial Policy</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="uppercase tracking-[0.2em] text-xs font-semibold mb-6 text-background/70">Support</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/page/help-center" className="hover:text-primary transition-colors">Help Center</Link></li>
              <li><Link to="/page/faq" className="hover:text-primary transition-colors">FAQ</Link></li>
              <li><Link to="/page/safety-security" className="hover:text-primary transition-colors">Safety & Security</Link></li>
              <li><Link to="/contact" className="hover:text-primary transition-colors">Contact</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-background/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-background/50">
          <p>© 2026 Kavyalekh. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            <Link to="/page/privacy-policy" className="hover:text-background transition-colors">Privacy</Link>
            <Link to="/page/terms-of-use" className="hover:text-background transition-colors">Terms</Link>
            <Link to="/page/cookie-policy" className="hover:text-background transition-colors">Cookies</Link>
            <Link to="/page/code-of-conduct" className="hover:text-background transition-colors">Code of Conduct</Link>
            <Link to="/page/accessibility-statement" className="hover:text-background transition-colors">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default HomeEnhanced;
